import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from '../shared/Modal';
import { Input } from '../shared/Input';
import { Button } from '../shared/Button';
import { PackageEditor } from './PackageEditor';
import { ProductImageUploader } from './ProductImageUploader';
import { NuevoProducto, Producto } from '../../types/catalogo';
import { useCategorias } from '../../hooks/useCategorias';
import { tieneNombresDuplicados, validarPaquete, validarStock } from '../../validation/validators';

interface Props {
  producto?: Producto;
  onClose: () => void;
  onSave: (datos: NuevoProducto) => void;
  guardando: boolean;
}

/** US-06: crear/editar producto, incluyendo sus paquetes de precio (FR-CAT-01/02/03).
 * La categoría se elige de la lista real (BR-CAT-09) — ya no es texto libre. */
export function ProductFormModal({ producto, onClose, onSave, guardando }: Props) {
  const { data: categorias } = useCategorias();
  const [datos, setDatos] = useState<NuevoProducto>(
    producto ?? { nombre: '', descripcion: '', imagenes: [], categoriaId: '', stock: 0, paquetes: [] },
  );

  const errorStock = validarStock(datos.stock);
  const erroresPaquetes = datos.paquetes.some((p) => validarPaquete(p) !== null);
  const duplicados = tieneNombresDuplicados(datos.paquetes);
  const sinCategorias = (categorias ?? []).length === 0;
  const puedeGuardar =
    !errorStock &&
    !erroresPaquetes &&
    !duplicados &&
    !sinCategorias &&
    datos.nombre.trim().length > 0 &&
    datos.categoriaId.trim().length > 0;

  return (
    <Modal titulo={producto ? 'Editar Producto' : 'Nuevo Producto'} onClose={onClose}>
      <Input
        label="Nombre"
        value={datos.nombre}
        onChange={(e) => setDatos({ ...datos, nombre: e.target.value })}
        placeholder="Ej. Rosario de Madera de Olivo"
      />
      <Input
        label="Descripción"
        value={datos.descripcion}
        onChange={(e) => setDatos({ ...datos, descripcion: e.target.value })}
        placeholder="Breve descripción del producto"
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-bold uppercase tracking-wide text-admin-ink-muted">Categoría</label>
          {sinCategorias ? (
            <p className="text-xs text-danger leading-relaxed">
              No hay categorías creadas todavía.{' '}
              <Link to="/admin/categorias" className="underline font-semibold">
                Crear una
              </Link>{' '}
              antes de agregar productos.
            </p>
          ) : (
            <select
              value={datos.categoriaId}
              onChange={(e) => setDatos({ ...datos, categoriaId: e.target.value })}
              className="border border-admin-border rounded-md px-3.5 py-2.5 text-sm"
            >
              <option value="" disabled>
                Selecciona una categoría
              </option>
              {(categorias ?? []).map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nombre}
                </option>
              ))}
            </select>
          )}
        </div>
        <Input
          label="Stock (unidades)"
          type="number"
          value={datos.stock}
          error={errorStock ?? undefined}
          onChange={(e) => setDatos({ ...datos, stock: Number(e.target.value) })}
        />
      </div>

      <ProductImageUploader
        imagenes={datos.imagenes}
        onChange={(imagenes) => setDatos({ ...datos, imagenes })}
      />

      <PackageEditor
        paquetes={datos.paquetes}
        onChange={(paquetes) => setDatos({ ...datos, paquetes })}
      />

      <div className="flex justify-end gap-3 pt-2 -mx-7 -mb-6 px-7 py-5 border-t border-admin-border mt-2">
        <Button variante="outline" onClick={onClose} type="button">
          Cancelar
        </Button>
        <Button
          onClick={() => onSave(datos)}
          disabled={!puedeGuardar || guardando}
          data-testid="product-form-save-button"
        >
          {guardando ? 'Guardando…' : 'Guardar producto'}
        </Button>
      </div>
    </Modal>
  );
}
